import React, { useState, useEffect } from "react";
import { useHistory } from 'react-router-dom';
import Loading from "../../component/Loading/Loading";
import AlertMsg from '../../component/AlertMsg/AlertMsg';
import { useSelector } from "react-redux";
import Pagination from "../../utils/Pagenation/Pagenation";
import useAxiosInstance from "../../services/useAxiosInstance";
import { Eye, Pencil, Trash2, ArrowLeft } from "lucide-react";
import GlassmorphicModal from "../../component/ReusableComponents/GlassmorphicModal";

function AddPlan() {
  const axiosInstance = useAxiosInstance();
  const initialFormData = {
    planRange: "",
    planType: "",
    planPeriod: "",
    description: "",
    displayOrder: "",
    price: "",
  };

  const communityId = useSelector((state) => state.auth.communityId);
  const userId = useSelector((state) => state.auth.userId);

  const [alert, setAlert] = useState({ visible: false, message: '', type: '' });

  const history = useHistory();

  const [formData, setFormData] = useState(initialFormData);
  const [charCount, setCharCount] = useState(0);
  const maxDescriptionLength = 200;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [planRanges, setPlanRanges] = useState([]);
  const [planTypes, setPlanTypes] = useState([]);
  const [planPeriods, setPlanPeriods] = useState([]);
  const [submittedPlans, setSubmittedPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editPlanId, setEditPlanId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPlanData = async () => {
      setLoading(true);
      try {
        const [planRangesResponse, planTypesResponse, planPeriodsResponse] = await Promise.all([
          axiosInstance.get(`/api/public/plan-range/all`),
          axiosInstance.get(`/api/public/plan-type/all`),
          axiosInstance.get(`/api/public/plan-period/all`),
        ]);
        setPlanRanges(planRangesResponse.data);
        setPlanTypes(planTypesResponse.data);
        setPlanPeriods(planPeriodsResponse.data);
      } catch (error) {
        console.error("Error fetching plan data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlanData();
    fetchSubmittedPlans();
  }, []);

  const fetchSubmittedPlans = () => {
    setLoading(true);
    axiosInstance
      .get(`/api/public/plandetail/getAll`)
      .then((response) => {
        const plansWithPrice = response.data.map((plan) => {
          return axiosInstance
            .get(`/api/public/plandetail/getPlanDetailByPlanId/${plan.planid}`)
            .then((res) => {
              return {
                ...plan,
                price: res.data.price || plan.price,
              };
            })
            .catch((error) => {
              console.error("Error fetching plan details:", error);
              return plan;
            });
        });

        Promise.all(plansWithPrice).then((results) => {
          setSubmittedPlans(results);
        });
      })
      .catch((error) => {
        console.error("Error fetching submitted plans:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    const { planRange, planType, planPeriod } = formData;
    if (planRange && planType && planPeriod) {
      const planTypeId = planTypes.find(
        (type) => type.planType === planType
      )?.id;
      const planRangeId = planRanges.find(
        (range) => range.planRange === planRange
      )?.id;
      const planPeriodId = planPeriods.find(
        (period) => period.planPeriod === planPeriod
      )?.id;

      if (planTypeId && planRangeId && planPeriodId) {
        setLoading(true);
        axiosInstance
          .get(
            `/api/public/plan/find/${planTypeId}/${planRangeId}/${planPeriodId}`
          )
          .then((response) => {
            setFormData((prevFormData) => ({
              ...prevFormData,
              price: response.data.price || "",
            }));
            setSelectedPlanId(response.data.planId);
          })
          .catch((error) => {
            console.error("Error fetching price:", error);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
  }, [formData.planRange, formData.planType, formData.planPeriod]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    let selectedOption = null;

    if (name === "description") {
      if (value.length <= maxDescriptionLength) {
        setCharCount(value.length);
        setFormData({
          ...formData,
          [name]: value,
        });
        setError("");
      } else {
        setError(
          `Description cannot exceed ${maxDescriptionLength} characters.`
        );
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });

      if (name === "planRange") {
        selectedOption = planRanges.find((range) => range.planRange === value);
      } else if (name === "planPeriod") {
        selectedOption = planPeriods.find((period) => period.planPeriod === value);
      } else if (name === "planType") {
        selectedOption = planTypes.find((type) => type.planType === value);
      }
    }

    if (selectedOption) {
      console.log(`${name} ID:`, selectedOption.id);
      setFormData({
        ...formData,
        [name]: value,
        [`${name}Id`]: selectedOption.id,
      });
    }
  };

  const handleAddOrUpdatePlan = () => {
    setLoading(true);

    if (
      formData.planRange === "" ||
      formData.planType === "" ||
      formData.planPeriod === "" ||
      formData.description === "" ||
      formData.displayOrder === "" ||
      formData.price === ""
    ) {
      setError("Please fill out all fields.");
      setLoading(false);
      return;
    }

    if (formData.description.length > maxDescriptionLength) {
      setError(`Description cannot exceed ${maxDescriptionLength} characters.`);
      setLoading(false);
      return;
    }

    const { planTypeId, planRangeId, planPeriodId } = formData;

    if (isEditing) {
      const requestDataFormatted = {
        activestatus: true,
        displayorder: formData.displayOrder,
        name: formData.description,
        plandetailid: editPlanId,
        planid: selectedPlanId,
        price: formData.price,
      };

      console.log(selectedPlanId);

      axiosInstance
        .put(`/api/public/plandetail/update`, requestDataFormatted,)
        .then((response) => {
          console.log("Update Response:", response.data);

          setFormData((prevFormData) => ({
            ...prevFormData,
            description: "",
            displayOrder: "",
          }));
          fetchSubmittedPlans();
          setIsSubmitted(true);
          setError("");
          setIsEditing(false);
          setEditPlanId(null);
          setLoading(false);
          setAlert({ visible: true, message: 'Plan details updated successfully!', type: 'success' });
        })
        .catch((error) => {
          console.error("Error updating plan details:", error);
          setAlert({ visible: true, message: 'Error updating plan details: Please try again', type: 'error' });
          setError("Error updating plan details. Please try again.");
          setLoading(false);
        });
    } else {
      axiosInstance
        .get(`/api/public/plan/find/${planTypeId}/${planRangeId}/${planPeriodId}`)
        .then((response) => {
          const planId = response.data.planId;
          console.log("Fetched Plan ID:", planId);

          const requestDataFormatted = {
            activestatus: true,
            displayorder: formData.displayOrder,
            name: formData.description,
            plandetailid: 0,
            planid: planId,
            price: formData.price,
          };
          console.log("Formatted Request Data:", requestDataFormatted);
          setLoading(true);
          axiosInstance
            .post(`/api/public/plandetail/add`, requestDataFormatted)
            .then((response) => {
              console.log("Post Response:", response.data);

              setFormData((prevFormData) => ({
                ...prevFormData,
                description: "",
                displayOrder: "",
              }));
              fetchSubmittedPlans();
              setIsSubmitted(true);
              setError("");
              setLoading(false);
              setAlert({ visible: true, message: 'New plan added successfully!', type: 'success' });
            })
            .catch((error) => {
              console.error("Error posting plan details:", error);
              setAlert({ visible: true, message: 'Error adding plan details: Please try again', type: 'error' });
              setError("Error posting plan details. Please try again.");
              setLoading(false);
            });
        })
        .catch((error) => {
          console.error("Error fetching planId:", error);
          setError("Error fetching planId. Please try again.");
          setLoading(false);
        });
    }
  };

  const handleCancel = () => {
    setFormData(initialFormData);
    setIsSubmitted(false);
    setError("");
    setIsEditing(false);
    setEditPlanId(null);
    setSelectedPlanId(null);
    history.goBack();
  };

  const handleDelete = (plan) => {
    setPlanToDelete(plan);
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = () => {
    setLoading(true);
    if (planToDelete) {
      axiosInstance
        .delete(
          `/api/public/plandetail/delete/${planToDelete.plandetailid}`)
        .then((response) => {
          console.log("Delete Response:", response.data);
          fetchSubmittedPlans();
          setShowDeleteConfirmation(false);
          setPlanToDelete(null);
        })
        .catch((error) => {
          console.error("Error deleting plan detail:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  };

  const handleViewDetails = (plan) => {
    setSelectedPlan(plan);
  };

  const handleEdit = (plan) => {
    setLoading(true);
    setFormData({
      planRange: plan.planrange,
      planType: plan.plantype,
      planPeriod: plan.planperiod,
      description: plan.name,
      displayOrder: plan.displayorder,
      price: plan.price,
    });
    setEditPlanId(plan.plandetailid);
    setSelectedPlanId(plan.planid);
    setIsEditing(true);
    setLoading(false);
  };

  const filteredPlans = submittedPlans.filter((plan) =>
    selectedPlanId ? plan.planid === selectedPlanId : true
  );

  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  const pageCount = Math.ceil(filteredPlans.length / itemsPerPage);

  const startIndex = currentPage * itemsPerPage;
  const endIndex = (currentPage + 1) * itemsPerPage;
  const currentPageData = filteredPlans.slice(startIndex, endIndex);

  // Delete modal footer
  const deleteModalFooter = (
    <div className="flex justify-end space-x-2">
      <button
        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
        onClick={() => setShowDeleteConfirmation(false)}
      >
        Cancel
      </button>
      <button
        className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
        onClick={confirmDelete}
      >
        Delete
      </button>
    </div>
  );

  // Plan details modal footer
  const planDetailsFooter = (
    <div className="flex justify-end">
      <button
        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        onClick={() => setSelectedPlan(null)}
      >
        Close
      </button>
    </div>
  );

  const renderTableRows = () => {
    return currentPageData.map((plan, index) => (
      <tr key={index} className="hover:bg-gray-50">
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{plan.name}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">{plan.displayorder}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex items-center justify-center gap-3">
          <button
            className="text-blue-500 hover:text-blue-700"
            onClick={() => handleViewDetails(plan)}
            title="View Details"
          >
            <Eye size={18} />
          </button>
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={() => handleEdit(plan)}
            title="Edit Plan"
          >
            <Pencil size={18} />
          </button>
          <button
            className="text-red-500 hover:text-red-700"
            onClick={() => handleDelete(plan)}
            title="Delete Plan"
          >
            <Trash2 size={18} />
          </button>
        </td>
      </tr>
    ));
  };

  return (
    <div className="bg-gray-50 min-h-screen md:p-4">
      {loading && <Loading />}
      {!loading && (
        <div className="max-w-7xl mx-auto">
          {alert.visible && (
            <AlertMsg
              message={alert.message}
              type={alert.type}
              onClose={() => setAlert({ ...alert, visible: false })}
            />
          )}

          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">{isEditing ? "Edit Plan" : "Add Plan Details"}</h1>
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center gap-2"
            >
              <ArrowLeft size={18} />
              Go Back
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plan Range <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="planRange"
                    name="planRange"
                    value={formData.planRange}
                    onChange={handleInputChange}
                    className={`w-full rounded-md border ${error && !formData.planRange ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="">Select Plan Range</option>
                    {planRanges.map((planRange) => (
                      <option key={planRange.id} value={planRange.planRange}>
                        {planRange.planRange}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plan Period <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="planPeriod"
                    name="planPeriod"
                    value={formData.planPeriod}
                    onChange={handleInputChange}
                    className={`w-full rounded-md border ${error && !formData.planPeriod ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="">Select Plan Period</option>
                    {planPeriods.map((planPeriod) => (
                      <option key={planPeriod.id} value={planPeriod.planPeriod}>
                        {planPeriod.planPeriod}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plan Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="planType"
                    name="planType"
                    value={formData.planType}
                    onChange={handleInputChange}
                    className={`w-full rounded-md border ${error && !formData.planType ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="">Select Plan Type</option>
                    {planTypes.map((planType) => (
                      <option key={planType.id} value={planType.planType}>
                        {planType.planType}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    className={`w-full rounded-md border ${error && !formData.price ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50`}
                    placeholder="Enter Price"
                    readOnly
                  />
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-6 mb-4">
                <div className="w-full md:w-1/2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className={`w-full rounded-md border ${error && !formData.description ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {charCount > 0 && (
                    <p className="text-xs text-gray-500 mt-1">{charCount}/{maxDescriptionLength} characters</p>
                  )}
                </div>

                <div className="w-full md:w-1/4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="displayOrder"
                    name="displayOrder"
                    value={formData.displayOrder}
                    onChange={handleInputChange}
                    className={`w-full rounded-md border ${error && !formData.displayOrder ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                </div>

                <div className="flex items-end w-full md:w-1/4">
                  <button
                    className="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                    onClick={handleAddOrUpdatePlan}
                  >
                    {isEditing ? "Update" : "Add"}
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md mb-4">
                  {error}
                </div>
              )}

              <div className="text-sm text-red-500 mt-2">* indicates required field</div>
            </div>
          </div>

          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800">Plan Details</h2>
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-400">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider text-center">Display Order</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {renderTableRows().length === 0 ? (
                    <tr>
                      <td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">No Data Found</td>
                    </tr>
                  ) : renderTableRows()}
                </tbody>
              </table>
            </div>

            <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Page {currentPage + 1} of {pageCount || 1}
              </div>
              <div className="flex-1 flex justify-end">
                <Pagination
                  currentPage={currentPage}
                  pageCount={pageCount || 1}
                  setCurrentPage={setCurrentPage}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <GlassmorphicModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        title="Confirm Delete"
        size="sm"
        footer={deleteModalFooter}
      >
        <p className="text-gray-700">
          Are you sure you want to delete this plan? This action is permanent and cannot be undone.
        </p>
      </GlassmorphicModal>

      {/* Plan Details Modal */}
      <GlassmorphicModal
        isOpen={!!selectedPlan}
        onClose={() => setSelectedPlan(null)}
        title="Plan Details"
        size="lg"
        footer={planDetailsFooter}
      >
        {selectedPlan && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan Range</label>
              <div className="w-full rounded-md border border-gray-300 bg-gray-50 px-4 py-2 text-gray-700">
                {selectedPlan?.planrange ? selectedPlan?.planrange : "No Plan Range"}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan Period</label>
              <div className="w-full rounded-md border border-gray-300 bg-gray-50 px-4 py-2 text-gray-700">
                {selectedPlan?.planperiod ? selectedPlan?.planperiod : "No Plan Period"}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan Type</label>
              <div className="w-full rounded-md border border-gray-300 bg-gray-50 px-4 py-2 text-gray-700">
                {selectedPlan?.plantype ? selectedPlan?.plantype : "No Plan Type"}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
              <div className="w-full rounded-md border border-gray-300 bg-gray-50 px-4 py-2 text-gray-700">
                {selectedPlan?.price ? selectedPlan?.price : "No Price"}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <div className="w-full rounded-md border border-gray-300 bg-gray-50 px-4 py-2 text-gray-700">
                {selectedPlan?.name ? selectedPlan?.name : "No Name"}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
              <div className="w-full rounded-md border border-gray-300 bg-gray-50 px-4 py-2 text-gray-700">
                {selectedPlan?.displayorder ? selectedPlan?.displayorder : "No Display Order"}
              </div>
            </div>
          </div>
        )}
      </GlassmorphicModal>
    </div>
  );
}

export default AddPlan;